import React, { useState } from 'react'
import { func } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useUser } from '@fs/zion-user'
import { trackEvent } from '@fs/zion-analytics'
import { css } from '@emotion/core'
import { TextField, ChoiceChips, ZionForm, Button, useStatusOverlay, useAtSize } from '@fs/zion-ui'
import { useTreeState, useTreeDispatch } from './state/TreeStore'
import AncestorAvatar from './AncestorAvatar'
import GedcomX, { isMale, isFemale } from '../shared/helpers/gedcom_X'
import {
  determineFormLabelsByGenderType,
  determineAncestorOwnershipLabels,
  determineChildByAtn,
  determineSpouse,
  determineRelationshipLevel,
} from './helpers/ancestor_Determinant'
import isValidForm from './helpers/treeFormValidator'
import PlaceInput from '../shared/components/PlaceInput/src/PlaceInput'
import sendPersonToServer from './helpers/treeEndpointsCaller'
import animations from '../shared/services/cssAnimations'

const inputDecorations = {
  male: '#01b6d1',
  female: '#bd0057',
  unknown: '#aaaaaa',
}

const infoEntryCss = css`
  width: 100%;
  height: 100%;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
`

const inputCss = css`
  width: 70%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  position: relative;
`

const ancestorImageContainer = css`
  position: relative;
  width: 100%;
`

const boyBackground = css`
  background: ${inputDecorations.male};
  width: 100%;
  height: 125px;
`

const girlBackground = css`
  background: ${inputDecorations.female};
  width: 100%;
  height: 125px;
`

const unkBackground = css`
  background: ${inputDecorations.unknown};
  width: 100%;
`

export default function AncestorEditor({ setIsTreeShowing, aamDispatch }) {
  const user = useUser()
  const atSize = useAtSize()
  const state = useTreeState()
  const treeDispatch = useTreeDispatch()

  const person = state.userTree[state?.anchorATN]
  const child = determineChildByAtn(state, state?.anchorATN)
  const spouse = determineSpouse(state, state?.anchorATN)

  const [t] = useTranslation()
  const gcx = GedcomX()

  const [currentGender, setCurrentGender] = useState(person.genderType)
  const [livingChipSelected, setLivingChipSelected] = useState(person.living)
  const showStatusoverlay = useStatusOverlay()
  const isNotMe = person.atn !== 1

  const btnContainer = css`
    display: flex;
    flex: 1;
    justify-content: center;
    button#save {
      margin: 5px;
    }
    button#tree {
      margin: 5px 0 5px 5px;
    }
    a#previous {
      margin-right: 10px;
      ${person.atn === 1 && 'visibility: hidden; pointer-events: none'};
    }
    a#next {
      margin-left: 10px;
      ${person.atn === 7 && 'visibility: hidden; pointer-events: none'};
      ${!person.personId && user.signedIn && 'visibility: hidden; pointer-events: none'};
    }
  `

  const textInfoInputCss = css`
    ${atSize({ default: 'width: 100%;', md: 'width: 700px;' })};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: 0 auto 20px auto;
    border-radius: 0 0 5px 5px;
    background: #fff;
    padding-top: 30px;
    height: 100%;
  `

  const ancestorTitle = css`
    ${atSize({ default: 'font-size: 1.6em', md: 'font-size: 2em' })};
    ${atSize({ default: 'padding: 1.2em', md: 'padding: 1em' })};
    font-weight: bold;
    text-align: center;
    position: relative;
    left: 0;
    right: 0;
    top: -15px;
    height: 228px;
    color: white;
    animation: ${animations('fade-in')} 2s ease;
  `

  const chipInput = css`
    ${atSize({ default: 'margin-left: 20px', lg: 'margin-left: 250px' })};
    width: 50%;
    padding-top: 50px;
    text-align: center;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    label[active=''] {
      ${isFemale(currentGender) && 'background-color: #bd0057'};
      ${isFemale(currentGender) && 'color: #fff'};
      ${isFemale(currentGender) && 'border: none'};
    }
    label[active='']:hover {
      ${isFemale(currentGender) && 'background-color: hsla(333, 100%, 35%, 1)'};
    }
  `

  const livingChoice = css`
    label[active=''] {
      ${isFemale(currentGender) && 'background-color: #bd0057'};
      ${isFemale(currentGender) && 'color: #fff'};
      ${isFemale(currentGender) && 'border: none'};
    }
    label[active='']:hover {
      ${isFemale(currentGender) && 'background-color: hsla(333, 100%, 35%, 1)'};
    }
  `

  const submitCss = css`
    ${atSize({ default: 'width: 100%;', md: 'width: 600px;' })};
    display: flex;
    margin: 0 auto 25px auto;
    flex-direction: column;
  `

  const determineAppropriateHighlight = () => {
    if (isMale(currentGender)) {
      return boyBackground
    }
    if (isFemale(currentGender)) {
      return girlBackground
    }

    return unkBackground
  }

  const handleSetGender = (selectedChip, setFieldValue) => {
    const newGenderType = selectedChip.value
    setFieldValue('person.genderType', newGenderType, false)

    setCurrentGender(newGenderType)
  }

  const handleSetLivingState = (selectedChip, setFieldValue) => {
    const isLiving = selectedChip.value === 'true' || selectedChip.value === true
    setFieldValue('person.living', isLiving, false)

    if (isLiving) {
      setFieldValue('person.dod', null, false)
      setFieldValue('person.pod', null, false)
      setFieldValue('person.podId', null, false)
    }

    setLivingChipSelected(isLiving)
  }

  const addOrUpdatePersonOnServer = async ({ payloadDetails, onFinishedFn }) => {
    try {
      if (user && user.signedIn && user.personId) {
        await sendPersonToServer({ payloadDetails })
      }
      onFinishedFn()
    } catch (err) {
      console.error(err)
      showStatusoverlay({
        message: 'An error has occurred',
        type: 'error',
        dismissable: true,
      })
    }
  }

  const validateForm = (values) => {
    const errorReport = isValidForm(values, state.includeLocation, state.atfGuestOptionSelected)
    const errors = { person: {} }

    Object.entries(errorReport?.person).forEach(([fieldName, value]) => {
      errors.person[fieldName] = t(value.key, value.defaultValue)
    })
    if (Object.keys(errors?.person).length === 0) {
      delete errors.person
    }

    return errors
  }

  const handleSubmit = (values, bag) => {
    validateForm(values)
    const payloadDetails = {
      person: values.person,
      child,
      spouse: (spouse && spouse.personId) ?? spouse,
      t,
    }

    const onFinishedFn = () => {
      bag.setSubmitting(false)
      bag.resetForm()
      treeDispatch({
        type: 'COMMIT_ANCESTOR',
        payload: {
          person: values.person,
        },
      })
      showStatusoverlay({
        message: t('atf.ancestor-editor.validation.success', 'Changes saved successfully'),
        type: 'success',
        dismissable: true,
      })
      setIsTreeShowing(true)
    }

    if (!user.signedIn) {
      values.person.displayName = `${values.person.name} ${values.person.surname}`
      aamDispatch({
        type: 'ADD_GUEST_ANCESTOR',
        data: values.person,
      })
    }

    if (user.signedIn && !child?.personId && values.person.atn !== 1) {
      showStatusoverlay({
        message: t(
          'atf.ancestor-editor.validation.missing-child',
          'Record not saved, ensure you add children before parents.'
        ),
        type: 'error',
        dismissable: true,
      })
    } else {
      addOrUpdatePersonOnServer({ payloadDetails, onFinishedFn })
    }
  }

  React.useEffect(() => {
    setLivingChipSelected(person.living)
  }, [person.living])

  React.useEffect(() => {
    setCurrentGender(person.genderType)
  }, [person.genderType])

  const labels = determineFormLabelsByGenderType(t, currentGender)
  const parentDetails = determineAncestorOwnershipLabels(person, child, currentGender)

  return (
    <ZionForm onSubmit={handleSubmit} initialValues={{ person }} validate={validateForm} enableReinitialize>
      {({ setFieldValue, isValid }) => (
        <>
          <div css={infoEntryCss}>
            <div css={determineAppropriateHighlight()}>
              <div css={ancestorTitle}>{t(parentDetails.key, parentDetails.defaultValue)}</div>
            </div>
            <div css={ancestorImageContainer}>
              <AncestorAvatar
                name="person.portraitThumbnailLink"
                fieldValue={person.portraitThumbnailLink}
                genderType={currentGender}
              />
            </div>
            <div css={textInfoInputCss}>
              <div css={chipInput}>
                <ChoiceChips
                  name="person.genderType"
                  onSelectionChange={(selectedChip) => handleSetGender(selectedChip, setFieldValue)}
                  chips={[
                    { label: t('atf.ancestor-editor.male', 'Male'), value: gcx.MALE, selected: isMale(currentGender) },
                    {
                      label: t('atf.ancestor-editor.female', 'Female'),
                      value: gcx.FEMALE,
                      selected: isFemale(currentGender),
                    },
                  ]}
                />
              </div>
              <div css={inputCss}>
                <TextField
                  name="person.name"
                  type="text"
                  placeholder={labels.placeholderFirstName}
                  label={t('atf.ancestor-editor.labels.first-name', 'First Name')}
                />
              </div>

              <div css={inputCss}>
                <TextField
                  name="person.surname"
                  type="text"
                  placeholder={labels.placeholderSurname}
                  label={labels.labelSurname}
                />
              </div>

              <div css={inputCss}>
                <TextField
                  type="number"
                  name="person.dob"
                  label={t('atf.ancestor-editor.labels.vitality-date-birth', '4-digit birth year')}
                  placeholder={t('atf.ancestor-editor.placeholders.example-date', 'ex: 1960')}
                />
              </div>

              {state.includeLocation && (
                <>
                  <div css={inputCss}>
                    <PlaceInput
                      fieldName="person.pob"
                      idFieldName="person.pobId"
                      fieldValue={person.pob}
                      idFieldValue={person.pobId}
                      label={t('atf.ancestor-editor.labels.vitality-place-birth', 'Place of birth')}
                      onOptionSelected={(el) => {
                        setFieldValue('person.pob', el.primary, true)
                        setFieldValue('person.pobId', `${el.placeId}`, true)
                      }}
                    />
                  </div>
                </>
              )}

              {isNotMe && !livingChipSelected && (
                <div css={inputCss}>
                  <TextField
                    type="number"
                    name="person.dod"
                    label={t('atf.ancestor-editor.labels.vitality-date-death', '4-digit death year')}
                    placeholder={t('atf.ancestor-editor.placeholders.example-date', 'ex: 1960')}
                  />
                </div>
              )}

              {isNotMe && !livingChipSelected && (
                <>
                  <div css={inputCss}>
                    <PlaceInput
                      fieldName="person.pod"
                      idFieldName="person.podId"
                      fieldValue={person.pod}
                      idFieldValue={person.podId}
                      label={t('atf.ancestor-editor.labels.vitality-place-death', 'Place of death')}
                      onOptionSelected={(el) => {
                        setFieldValue('person.pod', el.primary, true)
                        setFieldValue('person.podId', `${el.placeId}`, true)
                      }}
                    />
                  </div>
                </>
              )}

              <div css={livingChoice}>
                <ChoiceChips
                  name="person.living"
                  onSelectionChange={(selectedChip) => handleSetLivingState(selectedChip, setFieldValue)}
                  chips={[
                    {
                      label: t('atf.ancestor-editor.living', 'Living'),
                      value: 'true',
                      selected: livingChipSelected,
                    },
                    {
                      label: t('atf.ancestor-editor.deceased', 'Deceased'),
                      value: 'false',
                      selected: !livingChipSelected,
                      disabled: !isNotMe,
                    },
                  ]}
                />
              </div>

              <div css={submitCss}>
                <div css={btnContainer}>
                  <Button
                    id="save"
                    disabled={!isValid}
                    emphasis="high"
                    type="submit"
                    onClick={() =>
                      trackEvent({
                        event_name: `Add Tree Flow - saved ${determineRelationshipLevel(person.atn)}`,
                      })
                    }
                  >
                    {t('atf.ancestor-editor.save', 'Save')}
                  </Button>
                </div>
              </div>
              <div css={btnContainer}>
                <Button id="tree" emphasis="low" onClick={() => setIsTreeShowing(true)}>
                  {t('atf.ancestor-editor.cancel', 'Go Back To Your Family Tree')}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </ZionForm>
  )
}

AncestorEditor.propTypes = {
  setIsTreeShowing: func,
  aamDispatch: func,
}
